// F1 — מנוע התרגום של הפרונט.
//
// ★ למה מימוש עצמי ולא i18next: הפרונט נארז ונשלח כחבילת-עדכון ללקוחות, וכל
//   תלות חדשה היא עוד משהו שצריך להיבנות נכון אצל ג'וני. הצורך כאן הוא שטוח —
//   מפתח→מחרוזת עם הצבה של משתנים — ולכן ~120 שורות מכסות אותו במלואו. ה-API
//   (‏`t(key, vars)` + provider) תואם ל-i18next בכוונה, כך שהחלפה בעתיד לא
//   תיגע באף callsite.
//
// ★★ המצב מוחזק ברמת-המודול ולא ב-redux בכוונה: הוא נדרש גם מחוץ ל-React
//    (‏`redux/actions/*`, ‏`utils/*`, ובקשות-שרת שצריכות לצרף `lang`), ומעבר
//    ל-store היה מכריח את כל הצרכנים האלה להיות רכיבים.

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/he';
import 'dayjs/locale/ar';
import 'dayjs/locale/ru';
import 'dayjs/locale/en';
import 'dayjs/locale/am';

import { DEFAULT_LANG, isSupported, langMeta } from './config';
import he from './locales/he.json';
import ar from './locales/ar.json';
import ru from './locales/ru.json';
import en from './locales/en.json';
import am from './locales/am.json';

export { LANGS, LANG_CODES, DEFAULT_LANG, langMeta, dirOf } from './config';

const BUNDLES = { he, ar, ru, en, am };
const STORAGE_KEY = 'ans_lang_v1';

/* ------------------------------------------------------------------ *
 * state
 * ------------------------------------------------------------------ */

let currentLang = DEFAULT_LANG;
const listeners = new Set();

/**
 * סדר-העדיפויות לקביעת השפה ההתחלתית:
 *   1. ‏`?lang=` ב-URL  — כדי שלינק-SMS/דיפ-לינק יוכל לכפות שפה
 *   2. ‏localStorage    — הבחירה הקודמת של אותו אזרח
 *   3. עברית            — ברירת-המחדל
 *
 * ★ זיהוי-אוטומטי משפת-הדפדפן **לא** נכלל בכוונה (החלטת-משתמש): אזרח ישראלי
 *   עם דפדפן באנגלית היה מקבל אתר באנגלית — תלונה ודאית.
 */
function resolveInitialLang() {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('lang');
    if (isSupported(fromUrl)) return fromUrl;
  } catch (e) { /* URL לא תקין — ממשיכים */ }
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isSupported(saved)) return saved;
  } catch (e) { /* private mode */ }
  return DEFAULT_LANG;
}

export function getLang() {
  return currentLang;
}

/* ------------------------------------------------------------------ *
 * אילו שפות הלקוח הדליק (מגיע מ-`user/site-config`)
 * ------------------------------------------------------------------ */

/* ★★ ברירת-המחדל היא **רשימה ריקה** ולא "כל השפות", וזה מכוון:
   הבורר לא יוצג עד שהשרת יאשר במפורש אילו שפות פעילות. אילו ברירת-המחדל
   הייתה "הכול", כל לקוח קיים היה רואה בורר עם 5 שפות למשך שבריר-שנייה עד
   שהתשובה חוזרת — ואצל לקוח שלא הדליק כלום, הבזק של פיצ'ר שאינו קיים. */
let enabledLangs = [];
/* ⚠️ מונה-גרסה. ‏`setLangState(currentLang)` עם אותו ערך **אינו** מרנדר מחדש
   (React עוצר על ערך זהה), ולכן שינוי ברשימת-השפות בלי שינוי בשפה הנוכחית
   לא היה מגיע לבורר והוא היה נשאר מוסתר. */
let langsVersion = 0;

export function getEnabledLangs() {
  return enabledLangs;
}

/**
 * מעדכן את רשימת-השפות הפעילות.
 * ★★ אם השפה הנוכחית **אינה** ברשימה — חוזרים לעברית. אחרת אזרח שקיבל לינק
 *    ‏`?lang=ru` ללקוח שכיבה רוסית היה נתקע בממשק רוסי בלי שום דרך לצאת
 *    ממנו (הבורר עצמו לא היה מציג רוסית).
 */
export function setEnabledLangs(list) {
  const next = Array.isArray(list)
    ? list.filter((c) => isSupported(c) && c !== DEFAULT_LANG)
    : [];
  const same = next.length === enabledLangs.length && next.every((c, i) => c === enabledLangs[i]);

  enabledLangs = next;
  if (!same) { langsVersion += 1; }

  /* ★★ אכיפת-השפה רצה **תמיד**, גם כשהרשימה לא השתנתה.
     ⚠️ הבאג שזה סוגר (נתפס בבדיקה): הרשימה מתחילה ריקה, השרת מחזיר ריקה,
        ולכן `same===true` והפונקציה יצאה מוקדם — כך ש-`?lang=en` על לקוח
        שלא הדליק שום שפה **נשאר באנגלית**. ההגדרה הייתה המלצה ולא אכיפה. */
  const invalid = currentLang !== DEFAULT_LANG && next.indexOf(currentLang) === -1;
  if (invalid) {
    /* ⚠️ גם ה-localStorage מנוקה: אחרת הבחירה הפסולה הייתה חוזרת בכל רענון
       ומאלצת נפילה-לעברית שוב ושוב. */
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* private mode */ }
    setLang(DEFAULT_LANG);   // מודיע למאזינים בעצמו
    return;
  }
  if (same) { return; }
  listeners.forEach((fn) => { try { fn(currentLang); } catch (e) { /* צרכן אחד לא מפיל את השאר */ } });
}

export function getDir() {
  return langMeta(currentLang).dir;
}

/**
 * מחיל את השפה על ה-document עצמו.
 * ★★ ‏`lang` נדרש לקוראי-מסך ולהגיית-הטקסט (נגישות, ת"י 5568), ו-`dir` הוא
 *    מה שכל ה-CSS הלוגי (`margin-inline-start` וכו') וכללי `[dir="ltr"]`
 *    נשענים עליו. שניהם חייבים להשתנות יחד.
 */
function applyDocumentLang(lang) {
  const meta = langMeta(lang);
  try {
    const html = document.documentElement;
    html.setAttribute('lang', meta.code);
    html.setAttribute('dir', meta.dir);
  } catch (e) { /* SSR/‏jsdom */ }
  dayjs.locale(meta.dayjs);
}

export function setLang(lang) {
  if (!isSupported(lang) || lang === currentLang) return;
  currentLang = lang;
  try { localStorage.setItem(STORAGE_KEY, lang); } catch (e) { /* private mode */ }
  applyDocumentLang(lang);
  listeners.forEach((fn) => { try { fn(lang); } catch (e) { /* צרכן אחד לא מפיל את השאר */ } });
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/* ------------------------------------------------------------------ *
 * lookup
 * ------------------------------------------------------------------ */

function lookup(bundle, key) {
  if (!bundle) return undefined;
  // תמיכה גם במפתח מקונן ('form.submit') וגם במפתח שטוח שמכיל נקודות.
  if (Object.prototype.hasOwnProperty.call(bundle, key)) return bundle[key];
  let node = bundle;
  const parts = key.split('.');
  for (let i = 0; i < parts.length; i++) {
    if (node === null || typeof node !== 'object') return undefined;
    node = node[parts[i]];
  }
  return typeof node === 'string' ? node : undefined;
}

function interpolate(str, vars) {
  if (!vars) return str;
  return str.replace(/\{\{(\w+)\}\}/g, (m, name) =>
    (vars[name] === undefined || vars[name] === null ? m : String(vars[name])));
}

/**
 * ‏t('form.submit', { name: 'דנה' })
 *
 * ★ שרשרת-הנפילה: השפה הנוכחית → עברית → **המפתח עצמו**. החזרת המפתח (ולא
 *   מחרוזת ריקה) היא מכוונת: מפתח חסר נראה מיד על המסך בבדיקה, במקום להיעלם
 *   בשקט ולהשאיר כפתור ריק בייצור.
 */
export function t(key, vars) {
  if (!key) return '';
  const hit = lookup(BUNDLES[currentLang], key);
  if (typeof hit === 'string') return interpolate(hit, vars);
  const fallback = lookup(BUNDLES[DEFAULT_LANG], key);
  if (typeof fallback === 'string') return interpolate(fallback, vars);
  return key;
}

/* ------------------------------------------------------------------ *
 * React
 * ------------------------------------------------------------------ */

const I18nContext = createContext({ lang: DEFAULT_LANG, dir: 'rtl', t, setLang });

export function I18nProvider({ children }) {
  // ‏`v` = מונה-הגרסה של רשימת-השפות; ראה ההסבר ב-`langsVersion`.
  const [{ lang, v }, setState] = useState({ lang: currentLang, v: langsVersion });

  useEffect(() => subscribe(() => setState({ lang: currentLang, v: langsVersion })), []);

  const value = useMemo(() => ({
    lang,
    langs: enabledLangs,
    dir: langMeta(lang).dir,
    // ‏`t` נוצר-מחדש בכל החלפת-שפה כדי שרכיבים שצורכים אותו ירונדרו מחדש;
    // ‏`t` המיוצא ברמת-המודול הוא אותה פונקציה בדיוק, בלי התלות ב-React.
    t: (key, vars) => t(key, vars),
    setLang,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [lang, v]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** ‏const { t, lang, dir, setLang } = useI18n() */
export function useI18n() {
  return useContext(I18nContext);
}

/** קיצור לשימוש הנפוץ: ‏const t = useT() */
export function useT() {
  return useContext(I18nContext).t;
}

/* ------------------------------------------------------------------ *
 * boot
 * ------------------------------------------------------------------ */

// ★★ רץ בזמן ה-import, לפני הרינדור הראשון — אחרת הפריים הראשון היה נצבע
//    בכיווניות של index.html (rtl) וקופץ ל-LTR ברוסית/אנגלית.
currentLang = resolveInitialLang();
applyDocumentLang(currentLang);
